import {
  getHomesApiHomeGet,
  getHomeByIdApiHomeHomeIdGet,
  getHomeExpensesApiFinanceExpensesHomeHomeIdGet,
  getHomeMembersApiHomeHomeIdMembersGet,
  updateHomeApiHomeHomeIdPut,
  uploadHomeCoverApiHomeHomeIdCoverPost,
  removeHomeCoverApiHomeHomeIdCoverDelete,
  getHomeDashboardApiHomeHomeIdDashboardGet,
  getHomeTypesApiHomeTypesGet,
  updateHomeMembershipsApiHomeHomeIdMembershipsPatch,
} from '@/client';
import type {
  HomeResponse,
  MembershipResponse,
  HomeUpdate,
  HomeDashboardResponse,
  ExpenseResponse,
  HomeTypeResponse,
  MembershipUpdate,
} from '@/client/types.gen';
import { createApiError } from '@/lib/api-error';
import { httpClient } from '@/lib/http-client';

export async function getUserHomes(): Promise<HomeResponse[]> {
  const response = await getHomesApiHomeGet({
    client: httpClient,
    headers: { authorization: '' }, // Handled by interceptor
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return (response.data ?? []) as HomeResponse[];
}

export async function getHomeOverview(homeId: string): Promise<HomeDashboardResponse> {
  const response = await getHomeDashboardApiHomeHomeIdDashboardGet({
    client: httpClient,
    path: { home_id: homeId },
    headers: { authorization: '' }, // Handled by interceptor
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return response.data as HomeDashboardResponse;
}

export async function getHomeTypes(): Promise<HomeTypeResponse[]> {
  const response = await getHomeTypesApiHomeTypesGet({
    client: httpClient,
    headers: { authorization: '' }, // Handled by interceptor
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return (response.data ?? []) as HomeTypeResponse[];
}

export async function getHome(homeId: string): Promise<HomeResponse> {
  const response = await getHomeByIdApiHomeHomeIdGet({
    client: httpClient,
    path: { home_id: homeId },
    headers: { authorization: '' }, // Handled by interceptor
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return response.data as HomeResponse;
}
export async function updateHome(homeId: string, data: HomeUpdate): Promise<HomeResponse> {
  const response = await updateHomeApiHomeHomeIdPut({
    client: httpClient,
    path: { home_id: homeId },
    body: data,
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return response.data as HomeResponse;
}

export async function removeHomeCover(homeId: string): Promise<HomeResponse> {
  const response = await removeHomeCoverApiHomeHomeIdCoverDelete({
    client: httpClient,
    path: { home_id: homeId },
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return response.data as HomeResponse;
}

export async function uploadHomeCover(homeId: string, file: File): Promise<HomeResponse> {
  const response = await uploadHomeCoverApiHomeHomeIdCoverPost({
    client: httpClient,
    path: { home_id: homeId },
    body: { file },
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return response.data as HomeResponse;
}

export async function getHomeExpenses(homeId: string): Promise<ExpenseResponse[]> {
  const response = await getHomeExpensesApiFinanceExpensesHomeHomeIdGet({
    client: httpClient,
    path: { home_id: homeId },
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return (response.data ?? []) as ExpenseResponse[];
}

export async function getHomeMembers(homeId: string): Promise<MembershipResponse[]> {
  const response = await getHomeMembersApiHomeHomeIdMembersGet({
    client: httpClient,
    path: { home_id: homeId },
    headers: { authorization: '' }, // Handled by interceptor
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return (response.data ?? []) as MembershipResponse[];
}

/** Rewrites equity and/or expense shares for several members at once. */
export async function updateHomeMemberships(
  homeId: string,
  memberships: MembershipUpdate[],
): Promise<MembershipResponse[]> {
  const response = await updateHomeMembershipsApiHomeHomeIdMembershipsPatch({
    client: httpClient,
    path: { home_id: homeId },
    headers: { authorization: '' }, // Handled by interceptor
    body: { memberships },
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return (response.data ?? []) as MembershipResponse[];
}

// Moved to `lib/finance-api.ts` — the expenses screen and the Home Hub both read
// it, so it is no longer the home module's to own. Re-exported because the test
// harness mocks this module and several callers already import it from here.
export { getLedgerBalance } from '@/lib/finance-api';